import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import api from "../../lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Badge } from "@/components/ui/Badge";
import { Textarea } from "@/components/ui/Textarea";
import { InfoMessage } from "@/components/ui/InfoMessage";
import { ChevronLeft, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Eye, EyeOff, ArrowLeft, UserPlus, X } from "lucide-react";
import toast from "react-hot-toast";
import { validateEmail } from "../../lib/utils";

const EditClientPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    commissioned_by: "",
    is_active: true,
    notes: "",
    phone: "",
    contact_first_name: "",
    contact_last_name: "",
    site_names: [],
    report_names: [],
  });

  // Fetch client data
  const {
    data: client,
    isLoading,
    error,
  } = useQuery(
    ["client", id],
    async () => {
      const response = await api.get(`/clients/${id}`);
      return response.data.data;
    },
    {
      onSuccess: (clientData) => {
        setFormData({
          name: clientData.name || "",
          email: clientData.email || "",
          commissioned_by: clientData.commissioned_by || "",
          is_active: clientData.is_active ?? true,
          notes: clientData.notes || "",
          phone: clientData.phone || "",
          contact_first_name: clientData.contact_first_name || "",
          contact_last_name: clientData.contact_last_name || "",
          site_names: clientData.site_names || [],
          report_names: clientData.report_names || [],
        });
      },
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const updateMutation = useMutation((clientData) => api.put(`/clients/${id}`, clientData), {
    onSuccess: (response) => {
      const updated = response?.data?.data;
      if (updated) {
        // Immediately sync edit page cache so re-opening shows latest without refresh
        queryClient.setQueryData(["client", id], updated);
      }

      // Mark lists stale so they refetch on navigation
      queryClient.invalidateQueries(["clients"]);
      queryClient.invalidateQueries(["client", id]);

      toast.success("Client updated successfully");
      navigate("/clients");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update client");
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if all required fields are empty
    const allFieldsEmpty =
      !formData.name.trim() &&
      !formData.email.trim() &&
      !formData.contact_first_name.trim() &&
      !formData.contact_last_name.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before updating");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.name.trim()) {
      toast.error("Company name is required");
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      toast.error("Email address is required");
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      hasErrors = true;
    }

    if (!formData.contact_first_name.trim()) {
      toast.error("Contact first name is required");
      hasErrors = true;
    }

    if (!formData.contact_last_name.trim()) {
      toast.error("Contact last name is required");
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    updateMutation.mutate(formData);
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading client</div>;

  return (
    <div className="">
      <div className="w-full">
        <div className="slide-up">
          <form onSubmit={handleSubmit} noValidate>
            <div>
              {((formData.site_names && formData.site_names.length > 0) ||
                (formData.report_names && formData.report_names.length > 0)) && (
                  <InfoMessage>
                    Cannot delete this client while it is linked to sites or reports.
                  </InfoMessage>
                )}
            </div>
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-2">
                <Label
                  htmlFor="name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Company Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John"
                  className="w-full"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="commissioned_by">Commissioned By</Label>
                <Input
                  id="commissioned_by"
                  name="commissioned_by"
                  value={formData.commissioned_by}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="Commissioned by"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="contact_first_name">Company Contact First Name *</Label>
                <Input
                  id="contact_first_name"
                  name="contact_first_name"
                  value={formData.contact_first_name}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="John"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label htmlFor="contact_last_name">Company Contact Last Name *</Label>
                <Input
                  id="contact_last_name"
                  name="contact_last_name"
                  value={formData.contact_last_name}
                  className="w-full pl-3 text-xs font-medium text-gray-0"
                  onChange={handleChange}
                  placeholder="doe"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Company Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <Label className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Company phone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="text"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9879281010"
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes" className="pl-3 text-xs font-medium uppercase text-gray-0">
                Notes
              </Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Optional notes about the client"
              />
            </div>

            <div className="flex items-center py-6 space-x-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="rounded"
              />
              <Label htmlFor="is_active">Active Client</Label>
            </div>

            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/clients">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={updateMutation.isLoading}
                >
                  {updateMutation.isLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default EditClientPage;
